// compliance-backend has no query that lists AML entity types with their database IDs, so this
// mapping is hard-coded to match the `aml_entity` table seed data (auto-increment IDs assigned in
// insertion order by the `add_aml_entity_types` and `add_government_entities` migrations).
export const AML_ENTITY_TYPES: ReadonlyArray<{ id: number; name: string }> = [
  { id: 1, name: "Natural Person(s)" },
  { id: 2, name: "Trust" },
  { id: 3, name: "Corporation" },
  { id: 4, name: "Limited Liability Company (LLC)" },
  { id: 5, name: "Partnership" },
  { id: 6, name: "Private Company" },
  { id: 7, name: "Private Company Pension, Retirement or Income Plan" },
  { id: 8, name: "Fund" },
  { id: 9, name: "Custodian, Nominee or Agent" },
  { id: 10, name: "Regulated Institution" },
  { id: 11, name: "Public Company" },
  { id: 12, name: "Public Company Pension, Retirement or Income Plan" },
  { id: 13, name: "Sovereign Entity" },
  { id: 14, name: "State or Municipal Government Pension Plan" },
  { id: 15, name: "Non-Profit Organization" },
  { id: 16, name: "Foundation" },
  { id: 17, name: "Government Entity" },
];

export const AML_ENTITY_TYPE_ID_TO_NAME: Readonly<Record<number, string>> = Object.fromEntries(
  AML_ENTITY_TYPES.map(({ id, name }) => [id, name]),
);

export const AML_ENTITY_TYPE_NAME_TO_ID: Readonly<Record<string, number>> = Object.fromEntries(
  AML_ENTITY_TYPES.map(({ id, name }) => [name, id]),
);

export const AML_ENTITY_TYPE_NAMES: string[] = AML_ENTITY_TYPES.map(({ name }) => name);

// AML entity types a given legal entity type can resolve to. Entity types not
// listed here (e.g. individual/joint account types, "Other") are unconstrained
// and offer the full AML_ENTITY_TYPE_NAMES list.
const REGULATED_OR_FUND = [
  "Fund",
  "Regulated Institution",
  "Public Company",
  "Public Company Pension, Retirement or Income Plan",
];

export const ENTITY_TYPE_TO_AML_ENTITY_TYPES: Readonly<Record<string, string[]>> = {
  "Revocable Trust": ["Trust", "Regulated Institution"],
  "Irrevocable Trust": ["Trust", "Regulated Institution"],
  Trust: ["Trust", "Regulated Institution"],
  "Designated Activity Company (DAC)": ["Private Company", ...REGULATED_OR_FUND],
  "Société à Responsabilité Limitée (S.A.R.L.)": ["Private Company", ...REGULATED_OR_FUND],
  "Société Anonyme (S.A.)": ["Private Company", ...REGULATED_OR_FUND],
  "BVI Private Trust Company (PTC)": ["Private Company", ...REGULATED_OR_FUND],
  "Segregated Portfolio Company (SPC)": ["Private Company", ...REGULATED_OR_FUND],
  "Limited Liability Partnership (LLP)": ["Private Company", ...REGULATED_OR_FUND],
  "Private Limited Company (Ltd)": ["Private Company", ...REGULATED_OR_FUND],
  "Gesellschaft mit beschränkter Haftung (GmbH)": ["Private Company", ...REGULATED_OR_FUND],
  "Aktiengesellschaft (AG)": ["Private Company", ...REGULATED_OR_FUND],
  "Public Limited Company (PLC)": ["Private Company", ...REGULATED_OR_FUND],
  "Limited Liability Limited Partnership (LLLP)": ["Private Company", ...REGULATED_OR_FUND],
  "501(c)(3)": ["Non-Profit Organization", "Regulated Institution"],
  "Non-Profit": ["Non-Profit Organization", "Regulated Institution"],
  "Employee Benefit Plan, Trust, or Pension Plan": [
    "Private Company Pension, Retirement or Income Plan",
    "Regulated Institution",
    "Public Company",
    "Public Company Pension, Retirement or Income Plan",
  ],
  "Sovereign Entity": ["Sovereign Entity"],
  "Custodian, Nominee or Agent": ["Custodian, Nominee or Agent"],
  Corporation: ["Corporation", ...REGULATED_OR_FUND],
  "Limited Liability Company (LLC)": ["Limited Liability Company (LLC)", ...REGULATED_OR_FUND],
  Partnership: ["Partnership", ...REGULATED_OR_FUND],
  Foundation: ["Foundation", "Regulated Institution"],
  "State or Municipal Government Pension Plan": ["Government Entity"],
};

export const getAmlEntityTypeOptions = (entityType: string | undefined): string[] =>
  (entityType && ENTITY_TYPE_TO_AML_ENTITY_TYPES[entityType]) || AML_ENTITY_TYPE_NAMES;

export const resolveAmlEntityId = (
  amlEntityTypeName: string | undefined,
  includeAmlEntityType: boolean,
): number | undefined => {
  if (!includeAmlEntityType || !amlEntityTypeName) {
    return undefined;
  }
  return AML_ENTITY_TYPE_NAME_TO_ID[amlEntityTypeName];
};

// ProfileType.amlEntityType returns account_type when present, not the AML label.
// Prefer amlEntityTypeId → display name for form prefills.
export const resolveAmlEntityTypeNameFromId = (
  amlEntityTypeId: number | null | undefined,
): string => {
  if (amlEntityTypeId == null) {
    return "";
  }
  return AML_ENTITY_TYPE_ID_TO_NAME[amlEntityTypeId] ?? "";
};
